/**
 * listing-governance.ts
 *
 * Field Immutability Rules, Policy-Driven AAL3 Step-Up Triggers,
 * and Lifecycle Mutation Validation for Sovereign Commerce.
 */

import type { Product, ProductVariant, SchemaExtension } from '../commerce-contracts.js';

export interface ListingGovernancePolicy {
  readonly priceStepUpPercent: number; // e.g. 30 (30% change triggers MFA)
  readonly priceStepUpAbsoluteMinor: string; // e.g. "5000" ($50.00 change triggers MFA)
  readonly currency: string;
  readonly requireStepUpForBarcodeChange: boolean;
  readonly requireStepUpForTaxChange: boolean;
  readonly requireApprovalForChannelRevocation: boolean;
  readonly policyVersion: string;
}

export const DEFAULT_GOVERNANCE_POLICY: ListingGovernancePolicy = {
  priceStepUpPercent: 30,
  priceStepUpAbsoluteMinor: '5000',
  currency: 'USD',
  requireStepUpForBarcodeChange: true,
  requireStepUpForTaxChange: true,
  requireApprovalForChannelRevocation: true,
  policyVersion: '1.0.0',
};

export interface ListingPatchValidationResult {
  readonly allowed: boolean;
  readonly requiresSupervisorMfa: boolean;
  readonly mfaReasons: readonly string[];
  readonly violations: readonly string[];
  readonly warnings: readonly string[];
}

export const IMMUTABLE_ACTIVE_FIELDS = ['productId', 'sku', 'inventoryItemId'] as const;

/**
 * Validates product and variant patches against lifecycle immutability and AAL3 security policies.
 */
export function validateListingPatch(
  existingProduct: Product,
  productPatch: Partial<Product>,
  existingVariants: readonly ProductVariant[] = [],
  variantPatches: readonly { variantId: string; patch: Partial<ProductVariant> }[] = [],
  policy: ListingGovernancePolicy = DEFAULT_GOVERNANCE_POLICY
): ListingPatchValidationResult {
  const violations: string[] = [];
  const warnings: string[] = [];
  const mfaReasons: string[] = [];

  const isPublishedOrActive = existingProduct.status === 'active' || existingProduct.status === 'archived';

  // 1. Immutable Primary Identifier Validation
  if (productPatch.productId && productPatch.productId !== existingProduct.productId) {
    violations.push(`Field "productId" is permanently immutable. Found attempt to change "${existingProduct.productId}" to "${productPatch.productId}"`);
  }

  // 2. Sector Schema Immutability Post-Publication
  if (isPublishedOrActive && productPatch.schemaExtension) {
    const existingSector = existingProduct.schemaExtension?.sector;
    const proposedSector = productPatch.schemaExtension.sector;
    if (existingSector && proposedSector && existingSector !== proposedSector) {
      violations.push(`Sector classification is immutable post-publication (cannot mutate "${existingSector}" to "${proposedSector}"). Create a replacement product listing instead.`);
    }
  }

  // 3. Variant Immutability & Price Shifts
  for (const vp of variantPatches) {
    const existingVar = existingVariants.find((v) => v.variantId === vp.variantId);
    if (!existingVar) {
      warnings.push(`Variant "${vp.variantId}" is a newly added draft variant.`);
      continue;
    }

    // SKU Immutability Post-Publication
    if (isPublishedOrActive && vp.patch.sku && vp.patch.sku !== existingVar.sku) {
      violations.push(`Variant SKU is immutable post-publication (cannot mutate SKU "${existingVar.sku}" to "${vp.patch.sku}"). Use an inventory re-labeling movement or create a new variant.`);
    }

    // Inventory Item ID Immutability
    if (vp.patch.inventoryItemId && vp.patch.inventoryItemId !== existingVar.inventoryItemId) {
      violations.push(`Variant inventoryItemId is immutable. Found attempt to re-map "${existingVar.inventoryItemId}" to "${vp.patch.inventoryItemId}".`);
    }

    // Barcode Modification Check
    if (policy.requireStepUpForBarcodeChange && vp.patch.barcode && vp.patch.barcode !== existingVar.barcode) {
      mfaReasons.push(`Barcode GTIN reassignment on SKU ${existingVar.sku} (${existingVar.barcode || 'none'} -> ${vp.patch.barcode}) requires Supervisor MFA.`);
    }

    // Price Variance Check (AAL3 Step-Up Trigger)
    if (vp.patch.basePriceMinor && vp.patch.basePriceMinor !== existingVar.basePriceMinor) {
      const oldPrice = parseInt(existingVar.basePriceMinor || '0', 10);
      const newPrice = parseInt(vp.patch.basePriceMinor || '0', 10);
      const absDelta = Math.abs(newPrice - oldPrice);
      const absLimit = parseInt(policy.priceStepUpAbsoluteMinor, 10);

      const percentDelta = oldPrice > 0 ? (absDelta / oldPrice) * 100 : 100;

      if (percentDelta >= policy.priceStepUpPercent || absDelta >= absLimit) {
        const direction = newPrice < oldPrice ? 'decrease' : 'increase';
        mfaReasons.push(
          `Price shift of ${percentDelta.toFixed(1)}% (${direction} from ${(oldPrice / 100).toFixed(2)} to ${(newPrice / 100).toFixed(2)} ${existingVar.currency}) on SKU ${existingVar.sku} exceeds threshold (${policy.priceStepUpPercent}% or ${(absLimit / 100).toFixed(2)} ${policy.currency}) and requires Supervisor Step-Up.`
        );
      }
    }
  }

  // 4. Sales Channel Revocation Check
  if (isPublishedOrActive && productPatch.salesChannelIds && policy.requireApprovalForChannelRevocation) {
    const existingChannels = new Set(existingProduct.salesChannelIds || []);
    const proposedChannels = new Set(productPatch.salesChannelIds);
    for (const ch of existingChannels) {
      if (!proposedChannels.has(ch)) {
        mfaReasons.push(`Revoking product from active sales channel "${ch}" requires Supervisor confirmation.`);
      }
    }
  }

  const allowed = violations.length === 0;
  const requiresSupervisorMfa = mfaReasons.length > 0;

  return {
    allowed,
    requiresSupervisorMfa,
    mfaReasons,
    violations,
    warnings,
  };
}
